import React from 'react';
import { FormControl, Grid, Input, InputLabel, MenuItem, Select, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles } from '@mui/styles';
import { InsuranceConfig, InsuranceInput } from '../../modelView/finance/calculation/interfaces';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 300,
      maxWidth: 400,
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      margin: 2,
    },
    noLabel: {
      marginTop: theme.spacing(3),
    },
  }),
);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};

export default function InsuranceConfigOptions(props: {
  inputValues: InsuranceConfig;
  filteredValues?: InsuranceInput;
  setFilteredValues: (config: InsuranceInput) => void;
  disabled?: boolean;
  enableCreditOptions?: boolean;
}) {
  const classes = useStyles();

  const { t } = useTranslation();

  const handleChange = (key: keyof InsuranceInput, value: number | string) => {
    props.setFilteredValues({
      ...(props.filteredValues || {}),
      [key]: value,
    });
  };

  return (
    <>
      <Grid item>
        <FormControl
          className={classes.formControl}
          required
          error={!Boolean(props.filteredValues?.id)}
        >
          <InputLabel id='insurance-select-label'>{t('fieldName.insurance')}</InputLabel>
          <Select
            labelId='insurance-select-label'
            id='insurance-select'
            value={props.filteredValues?.id?.toString() || ''}
            onChange={(event) => handleChange('id', event.target.value)}
            input={<Input />}
            MenuProps={MenuProps}
            disabled={props.disabled}
          >
            {props.inputValues.insurance.map((insurance) => (
              <MenuItem
                key={insurance.key}
                value={insurance.key}
              >
                {insurance.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl
          className={classes.formControl}
          required
          error={!Boolean(props.filteredValues?.packageId)}
        >
          <InputLabel id='insurance-package-select-label'>{t('fieldName.insurancePackage')}</InputLabel>
          <Select
            labelId='insurance-package-select-label'
            id='insurance-package-select'
            value={props.filteredValues?.packageId?.toString() || ''}
            onChange={(event) => handleChange('packageId', event.target.value)}
            input={<Input />}
            MenuProps={MenuProps}
            disabled={props.disabled}
          >
            {props.inputValues.insurance
              .find((item) => item.key === props.filteredValues?.id)
              ?.packageOptions?.map((insurancePackage) => (
                <MenuItem
                  key={insurancePackage.key}
                  value={insurancePackage.key}
                >
                  {insurancePackage.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl
          className={classes.formControl}
          required
          error={!Boolean(props.filteredValues?.deductible)}
        >
          <InputLabel id='insurance-deductible-select-label'>{t('fieldName.insuranceDeductible')}</InputLabel>
          <Select
            labelId='insurance-deductible-select-label'
            id='insurance-deductible-select'
            value={props.filteredValues?.deductible?.toString() || ''}
            onChange={(event) => handleChange('deductible', parseInt(event.target.value))}
            input={<Input />}
            MenuProps={MenuProps}
            disabled={props.disabled}
          >
            {props.inputValues.deductible.map((deductible) => (
              <MenuItem
                key={deductible}
                value={deductible}
              >
                {deductible}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl
          className={classes.formControl}
          required
          error={!Boolean(props.filteredValues?.windshieldLimit)}
        >
          <InputLabel id='insurance-windshield-limit-select-label'>
            {t('fieldName.insuranceWindshieldLimit')}
          </InputLabel>
          <Select
            labelId='insurance-windshield-limit-select-label'
            id='insurance-windshield-limit-select'
            value={props.filteredValues?.windshieldLimit?.toString() || ''}
            onChange={(event) => handleChange('windshieldLimit', parseInt(event.target.value))}
            input={<Input />}
            MenuProps={MenuProps}
            disabled={props.disabled}
          >
            {props.inputValues.windshieldLimit.map((limit) => (
              <MenuItem
                key={limit}
                value={limit}
              >
                {limit}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {props.enableCreditOptions && (
        <>
          <Grid item>
            <FormControl
              className={classes.formControl}
              required
              error={!Boolean(props.filteredValues?.clientAge)}
            >
              <InputLabel id='client-age-select-label'>{t('fieldName.clientAge')}</InputLabel>
              <Select
                labelId='client-age-select-label'
                id='client-age-select'
                value={props.filteredValues?.clientAge?.toString() || ''}
                onChange={(event) => handleChange('clientAge', event.target.value)}
                input={<Input />}
                MenuProps={MenuProps}
                disabled={props.disabled}
              >
                {props.inputValues.clientAge?.map((age) => (
                  <MenuItem
                    key={age.key}
                    value={age.key}
                  >
                    {age.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl
              className={classes.formControl}
              required
              error={!Boolean(props.filteredValues?.claimFreeMonths)}
            >
              <InputLabel htmlFor='claim-free-months'>{t('fieldName.claimFreeMonths')}</InputLabel>
              <Input
                id='claim-free-months'
                type='number'
                value={props.filteredValues?.claimFreeMonths || ''}
                onChange={(event) => handleChange('claimFreeMonths', parseInt(event.target.value))}
                disabled={props.disabled}
              />
            </FormControl>
          </Grid>
        </>
      )}
    </>
  );
}
